import React, { FC, FormEvent, useEffect, useRef, useState } from 'react';
import chevron_down from 'assets/icons/chevron_down.svg';
import axiosInstance from 'config/axiosInstance';
import { DialogProps, NewDataField } from './dialogInterface';
import { useTranslation } from 'react-i18next';

const EditDialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  confirmLabel,
  cancelLabel,
  fields,
  handleSubmit,
  deviceId,
  attendanceId,
  vehicleId,
  taskSessionId, // Add TaskSessionId
}) => {
  const { t } = useTranslation('attendance');
  const dialogRef = useRef<HTMLDivElement>(null);
  const [newData, setNewData] = useState<NewDataField>(
    fields.reduce((acc, field) => {
      acc[field.name] = field.value || null;
      return acc;
    }, {} as NewDataField)
  );

  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});

  useEffect(() => {
    if (isOpen) {
      // Fetch Device Data
      if (deviceId) {
        const fetchDeviceData = async () => {
          try {
            const response = await axiosInstance.get(
              `/v1/devices/${deviceId}/`
            );
            const deviceData = response.data;

            const populatedData = fields.reduce((acc, field) => {
              if (field.name !== 'status') {
                acc[field.name] = deviceData[field.name] || null;
              } else {
                acc[field.name] =
                  deviceData.status === 'using'
                    ? `${deviceData.status}:${deviceData.user}`
                    : deviceData.status;
              }
              return acc;
            }, {} as NewDataField);
            setNewData(populatedData);
          } catch (error) {
            console.error('Failed to fetch device data:', error);
          }
        };
        fetchDeviceData();
      }
      // Fetch Attendance Data
      else if (attendanceId) {
        const fetchAttendanceData = async () => {
          try {
            const response = await axiosInstance.get(
              `/v1/attendances/${attendanceId}/`
            );
            const attendanceData = response.data;

            const populatedData = fields.reduce((acc, field) => {
              acc[field.name] =
                attendanceData[field.name] || field.value || null;
              return acc;
            }, {} as NewDataField);
            setNewData(populatedData);
          } catch (error) {
            console.error('Failed to fetch attendance data:', error);
          }
        };
        fetchAttendanceData();
      }
      // Fetch Vehicle Data
      else if (vehicleId) {
        const fetchVehicleData = async () => {
          try {
            const response = await axiosInstance.get(
              `/v1/vehicles/${vehicleId}/`
            );
            const vehicleData = response.data;

            const populatedData = fields.reduce((acc, field) => {
              acc[field.name] = vehicleData[field.name] || field.value || null;
              return acc;
            }, {} as NewDataField);
            setNewData(populatedData);
          } catch (error) {
            console.error('Failed to fetch vehicle data:', error);
          }
        };
        fetchVehicleData();
      }
      // Fetch Task Session Data
      else if (taskSessionId) {
        const fetchTaskSessionData = async () => {
          try {
            const response = await axiosInstance.get(
              `/v1/task_sessions/${taskSessionId}/`
            );
            const taskSessionData = response.data;

            const populatedData = fields.reduce((acc, field) => {
              acc[field.name] =
                taskSessionData[field.name] || field.value || null;
              return acc;
            }, {} as NewDataField);
            setNewData(populatedData);
          } catch (error) {
            console.error('Failed to fetch task session data:', error);
          }
        };
        fetchTaskSessionData();
      }
    }
  }, [deviceId, attendanceId, vehicleId, taskSessionId, isOpen, fields]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    let updatedData = { ...newData, [name]: value };

    setNewData(updatedData);
  };

  const validateDates = () => {
    const checkInDateTime =
      newData.check_in_date && newData.check_in_time
        ? new Date(`${newData.check_in_date} ${newData.check_in_time}`)
        : null;
    const checkOutDateTime =
      newData.check_out_date && newData.check_out_time
        ? new Date(`${newData.check_out_date} ${newData.check_out_time}`)
        : null;

    let validationErrors: { [key: string]: string | null } = {};

    if (checkInDateTime && checkOutDateTime) {
      if (checkOutDateTime < checkInDateTime) {
        validationErrors.check_out_time = t('edit.error_time');
        if (newData.check_out_date !== newData.check_in_date) {
          validationErrors.check_out_date = t('edit.error_date');
        }
      } else if (
        checkOutDateTime.toDateString() === checkInDateTime.toDateString() &&
        newData.check_out_time &&
        newData.check_in_time &&
        newData.check_out_time < newData.check_in_time
      ) {
        validationErrors.check_out_time = t('edit.error_time');
      }
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleFormSubmit = async (e: FormEvent<Element>) => {
    e.preventDefault();

    if (!validateDates()) {
      return;
    }

    const editFormData: any = { ...newData };

    await handleSubmit(e, editFormData);

    setNewData(
      fields.reduce((acc, field) => {
        acc[field.name] = field.value || null;
        return acc;
      }, {} as NewDataField)
    );
  };

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id="modal-headline"
                  >
                    {title}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <form onSubmit={handleFormSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 m-4 sm:m-6">
                    {fields.map((field, index) => (
                      <div key={index}>
                        <label className="block mb-1 text-sm font-regular text-neutral/100">
                          {field.label}{' '}
                          <span className="text-error">
                            {field.isRequired === true && '*'}
                          </span>
                        </label>
                        {field.type === 'select' ? (
                          <div className="relative">
                            <select
                              name={field.name}
                              className={`w-full mt-1 p-2 border ${
                                errors[field.name]
                                  ? 'border-red-500'
                                  : 'border-neutral/50'
                              } text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-4 appearance-none`}
                              required={field.isRequired}
                              value={newData[field.name] || ''}
                              onChange={handleChange}
                            >
                              {field.options?.map((option) => (
                                <option value={option.value} id={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral/30 pr-4 ">
                              <img src={chevron_down} alt="chevron_down_icon" />
                            </div>
                          </div>
                        ) : (
                          <input
                            type={field.type}
                            name={field.name}
                            className={`w-full mt-1 p-2 border ${
                              errors[field.name]
                                ? 'border-red-500'
                                : 'border-neutral/50'
                            } text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-4 appearance-none`}
                            required={field.isRequired}
                            placeholder={
                              field.type === 'date' ? 'yyyy/mm/dd' : ''
                            }
                            value={newData[field.name] || ''}
                            onChange={handleChange}
                          />
                        )}
                        {errors[field.name] && (
                          <span className="text-error">
                            {errors[field.name]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                      onClick={onClose}
                    >
                      {cancelLabel}
                    </button>
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12"
                    >
                      {confirmLabel}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDialog;
